import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  Brain,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  HeartHandshake,
  Info,
  Sparkles,
} from 'lucide-react';
import {
  EI_DOMAIN_META,
  EI_QUESTIONS,
  scoreEIAssessment,
  saveEIAssessmentSnapshot,
  type EIAssessmentAnswer,
  type EIAssessmentDomainKey,
} from '../lib/ei-assessment';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth-context';
import { saveEIAssessmentSnapshotServer } from '../lib/signature-lab-api';
import { cn } from '../components/ui/utils';

const LIKERT_CHOICES: Array<{ value: number; label: string; hint: string }> = [
  { value: 1, label: 'Not really me', hint: 'Rarely true' },
  { value: 2, label: 'Hardly ever', hint: 'Seldom' },
  { value: 3, label: 'Sometimes', hint: 'It depends' },
  { value: 4, label: 'Pretty often', hint: 'Mostly true' },
  { value: 5, label: 'Yes, that’s me', hint: 'Almost always' },
];

const DOMAIN_CHIP: Record<EIAssessmentDomainKey, string> = {
  selfAwareness:
    'border-violet-200 bg-violet-50 text-violet-900 dark:border-violet-800 dark:bg-violet-950/60 dark:text-violet-100',
  selfRegulation:
    'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950/60 dark:text-sky-100',
  empathy: 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950/60 dark:text-rose-100',
  communication:
    'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-100',
  repair:
    'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-100',
};

const DOMAIN_BAR: Record<EIAssessmentDomainKey, string> = {
  selfAwareness: 'bg-violet-500',
  selfRegulation: 'bg-sky-500',
  empathy: 'bg-rose-500',
  communication: 'bg-amber-500',
  repair: 'bg-emerald-500',
};

export default function EIAssessmentJourney() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitted, setSubmitted] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const completion = useMemo(() => {
    return Math.round((Object.keys(answers).length / EI_QUESTIONS.length) * 100);
  }, [answers]);

  const currentQuestion = EI_QUESTIONS[activeStep];
  const domainMeta = currentQuestion ? EI_DOMAIN_META[currentQuestion.domain] : null;

  const result = useMemo(() => {
    if (!submitted) return null;
    const payload: Array<EIAssessmentAnswer> = Object.entries(answers).map(([questionId, value]) => ({
      questionId,
      value,
    }));
    return scoreEIAssessment(payload);
  }, [answers, submitted]);

  const submit = async () => {
    if (Object.keys(answers).length !== EI_QUESTIONS.length) {
      toast.error('Please answer every question before finishing.');
      return;
    }
    setSubmitted(true);
    const payload: Array<EIAssessmentAnswer> = Object.entries(answers).map(([questionId, value]) => ({
      questionId,
      value,
    }));
    const computed = scoreEIAssessment(payload);
    saveEIAssessmentSnapshot(computed);
    if (accessToken) {
      try {
        await saveEIAssessmentSnapshotServer(accessToken, computed as unknown as Record<string, unknown>);
      } catch {
        // Local snapshot remains the fallback source.
      }
    }
    toast.success('Nice work — your personalized path is saved.');
  };

  const setAnswer = (questionId: string, value: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const goNext = () => {
    if (!currentQuestion) return;
    if (answers[currentQuestion.id] == null) {
      toast.message('Tap an answer to continue', { description: 'Choose what fits you best right now.' });
      return;
    }
    if (activeStep < EI_QUESTIONS.length - 1) {
      setActiveStep((s) => s + 1);
    } else {
      void submit();
    }
  };

  const goBack = () => {
    if (activeStep > 0) setActiveStep((s) => s - 1);
  };

  const domainOrder: EIAssessmentDomainKey[] = [
    'selfAwareness',
    'selfRegulation',
    'empathy',
    'communication',
    'repair',
  ];

  return (
    <div className="min-h-screen tension-page-bg">
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 space-y-6">
        {/* Guide */}
        <Card className="overflow-hidden border-violet-200/80 bg-gradient-to-br from-white via-violet-50/40 to-fuchsia-50/30 p-0 shadow-sm dark:border-violet-900/50 dark:from-neutral-950 dark:via-violet-950/20 dark:to-neutral-950">
          <div className="border-b border-violet-100/80 bg-white/60 px-5 py-4 dark:border-violet-900/40 dark:bg-neutral-900/40">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-sm">
                <HeartHandshake className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
                  Relationship Intelligence check-in
                </h1>
                <p className="mt-1 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
                  This short assessment measures how you tend to show up in relationships—not as a label, but as a
                  snapshot you can grow from. It powers tips inside TENSION that match{' '}
                  <strong className="font-medium text-neutral-800 dark:text-neutral-200">your</strong> strengths and
                  growth edges.
                </p>
              </div>
            </div>
          </div>
          <div className="space-y-3 px-5 py-4 text-sm text-neutral-700 dark:text-neutral-300">
            <p className="flex items-start gap-2">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-violet-600 dark:text-violet-400" aria-hidden />
              <span>
                <strong className="text-neutral-900 dark:text-neutral-100">What you’ll do:</strong> ten quick taps
                across five emotional skill areas—knowing yourself, staying steady, empathy, clear communication, and
                repair after tension.
              </span>
            </p>
            <p className="flex items-start gap-2">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
              <span>
                <strong className="text-neutral-900 dark:text-neutral-100">What you get:</strong> a simple score, a
                view of each area, and a few focused next steps—not therapy, just a clearer path for practice in the
                app.
              </span>
            </p>
            <p className="rounded-lg bg-neutral-100/80 px-3 py-2 text-xs text-neutral-600 dark:bg-neutral-800/80 dark:text-neutral-400">
              Takes about three minutes. Answer honestly—there are no wrong answers. You can retake anytime.
            </p>
          </div>
        </Card>

        {!submitted && (
          <>
            <Card className="p-5 sm:p-6">
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <Button variant="outline" size="sm" onClick={() => navigate('/home#relationship-intelligence')}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Question {activeStep + 1} of {EI_QUESTIONS.length}
                </p>
              </div>
              <div className="mb-6 h-2 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 transition-all duration-300 ease-out"
                  style={{ width: `${completion}%` }}
                />
              </div>

              {currentQuestion && domainMeta && (
                <div className="space-y-6">
                  <div
                    className={cn(
                      'inline-flex max-w-full flex-col gap-1 rounded-2xl border px-4 py-3 sm:flex-row sm:items-center sm:gap-3',
                      DOMAIN_CHIP[currentQuestion.domain],
                    )}
                  >
                    <span className="text-xs font-semibold uppercase tracking-wide opacity-80">
                      {domainMeta.title}
                    </span>
                    <span className="hidden text-neutral-400 sm:inline" aria-hidden>
                      ·
                    </span>
                    <span className="text-sm opacity-95">{domainMeta.tagline}</span>
                  </div>

                  <p className="text-lg font-medium leading-snug text-neutral-900 dark:text-neutral-50 sm:text-xl">
                    {currentQuestion.prompt}
                  </p>

                  <div className="grid gap-2 sm:gap-2.5" role="radiogroup" aria-label="Your answer">
                    {LIKERT_CHOICES.map((choice) => {
                      const selected = answers[currentQuestion.id] === choice.value;
                      return (
                        <button
                          key={choice.value}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => setAnswer(currentQuestion.id, choice.value)}
                          className={cn(
                            'flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3.5 text-left transition-all',
                            'hover:border-violet-300 hover:bg-violet-50/50 dark:hover:border-violet-700 dark:hover:bg-violet-950/30',
                            selected
                              ? 'border-violet-500 bg-violet-50 shadow-sm ring-2 ring-violet-500/30 dark:border-violet-500 dark:bg-violet-950/50'
                              : 'border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900/50',
                          )}
                        >
                          <div>
                            <span className="block font-medium text-neutral-900 dark:text-neutral-100">
                              {choice.label}
                            </span>
                            <span className="text-xs text-neutral-500 dark:text-neutral-400">{choice.hint}</span>
                          </div>
                          <span
                            className={cn(
                              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
                              selected
                                ? 'bg-violet-600 text-white'
                                : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
                            )}
                          >
                            {choice.value}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={goBack}
                      disabled={activeStep === 0}
                      className="gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button type="button" onClick={goNext} className="gap-1 bg-violet-600 hover:bg-violet-700">
                      {activeStep >= EI_QUESTIONS.length - 1 ? 'Finish & see results' : 'Next'}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Domain map — quick orientation */}
            <Card className="p-5">
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">The five areas</p>
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                Tap through the questions above—each area has two prompts.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {domainOrder.map((key) => {
                  const meta = EI_DOMAIN_META[key];
                  const qs = EI_QUESTIONS.filter((q) => q.domain === key);
                  const done = qs.every((q) => answers[q.id] != null);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        const firstIdx = EI_QUESTIONS.findIndex((q) => q.domain === key);
                        if (firstIdx >= 0) setActiveStep(firstIdx);
                      }}
                      className={cn(
                        'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                        DOMAIN_CHIP[key],
                        done && 'ring-2 ring-offset-2 ring-emerald-500/60 dark:ring-offset-neutral-950',
                      )}
                    >
                      {meta.title}
                      {done ? ' ✓' : ''}
                    </button>
                  );
                })}
              </div>
            </Card>
          </>
        )}

        {result && (
          <Card className="space-y-5 p-6">
            <div className="flex items-center gap-2">
              <Brain className="h-6 w-6 text-violet-600" />
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50">Your snapshot</h2>
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Scores reflect this moment—not fixed traits. Use them to pick one habit to practice this week.
            </p>
            <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-5 dark:border-violet-900 dark:from-violet-950/40 dark:to-neutral-950">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">Overall</p>
              <p className="mt-1 text-4xl font-bold tabular-nums text-violet-700 dark:text-violet-300">
                {result.overallScore}
                <span className="text-lg font-semibold text-neutral-400">/100</span>
              </p>
            </div>
            <div className="space-y-4">
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Skill areas</p>
              <div className="grid gap-3">
                {domainOrder.map((domain) => {
                  const score = result.domainScores[domain];
                  const meta = EI_DOMAIN_META[domain];
                  return (
                    <div
                      key={domain}
                      className="rounded-xl border border-neutral-200 bg-white/80 p-4 dark:border-neutral-700 dark:bg-neutral-900/60"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-neutral-900 dark:text-neutral-100">{meta.title}</p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">{meta.tagline}</p>
                        </div>
                        <span className="text-lg font-semibold tabular-nums text-neutral-800 dark:text-neutral-200">
                          {score}
                        </span>
                      </div>
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
                        <div
                          className={cn('h-full rounded-full transition-all', DOMAIN_BAR[domain])}
                          style={{ width: `${score}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Suggested next steps</p>
              {result.path.map((item) => (
                <p key={item} className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <span>{item}</span>
                </p>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button asChild className="bg-violet-600 hover:bg-violet-700">
                <Link to="/insights">View in Insights</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/home#relationship-intelligence">Back to Relationship Intelligence</Link>
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
